import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, useNavigation } from "contexts";
import { useWorkflow } from "contexts/components";
import { MessageType, WorkflowType } from "types";
import { stepRegistry } from "components/workflow/utils";
import { StepComponentRef } from "utils/helpers";
import { Enums } from "utils";

export const useFlow = <T>(
  initFlow: WorkflowType,
  onSubmitCallback?: (name: string, data: T) => void
) => {
  const stepRef = useRef<StepComponentRef>(null);
  const navigate = useNavigate();

  const { setFeedback } = useApp();
  const { goBack } = useNavigation();
  const { current, flows, data, nextStep, popFlow, prevStep, pushFlow, updateData } =
    useWorkflow<T>();

  const StepComponent =
    current && stepRegistry[current.name]
      ? stepRegistry[current.name][current.step - 1]
      : null;

  const [direction, setDirection] = useState<"next" | "prev">("next");

  useEffect(() => {
    pushFlow(initFlow);
    return () => popFlow();
  }, []);

  const getVariants = (dir: "next" | "prev") => ({
    initial: { opacity: 0, x: dir === "next" ? 100 : -100, scale: 0.9 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: dir === "next" ? -100 : 100 },
  });

  const handlePrev = () => {
    setDirection("prev");
    if (flows.length > 1 && current?.step === 1) return popFlow();
    if (flows.length === 1 && current?.step === 1) return goBack();
    prevStep();
  };

  const handleSkip = async (e: React.FormEvent) => {
    let updatedData = data;

    if (current.step === (current.end || current.count - 1)) {
      onSubmitCallback?.(current.name, updatedData);
    }

    if (current && current.step === current.count) {
      if (flows.length > 1) return popFlow();
      if (current.route) return navigate(current.route);
    } else {
      setDirection("next");
      nextStep();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let updatedData = data;

    const validation = await stepRef.current?.validate<T>();
    if (validation) {
      if (!validation.isValid) {
        return setFeedback({
          severity: Enums.EnumFeedback.Warning,
          message: validation.message,
        } as MessageType);
      }
      updatedData = validation.data || data;
    }

    // if (StepComponent && (StepComponent as any).onBranch) {
    //   (StepComponent as any).onBranch(pushFlow);
    //   return;
    // }

    if (current.step === (current.end || current.count - 1)) {
      onSubmitCallback?.(current.name, updatedData);
    }

    if (current && current.step === current.count) {
      if (flows.length > 1) return popFlow();
      if (current.route) return navigate(current.route);
    } else {
      setDirection("next");
      nextStep();
    }
  };

  return {
    current,
    data,
    direction,
    getVariants,
    handlePrev,
    handleSkip,
    handleSubmit,
    nextStep,
    popFlow,
    prevStep,
    pushFlow,
    step: current?.step || 1,
    StepComponent,
    stepRef,
    steps: current?.count || 0,
    updateData,
  };
};